import { zodResolver } from '@hookform/resolvers/zod';
import type { SelectChangeEvent } from '@mui/material';
import { Box, FormControl, FormHelperText, InputAdornment, InputBase, InputLabel, MenuItem, Select, TextField, styled } from '@mui/material';
import type { DynamicInvoicePageRevisionedLineItem } from '@weft/api-definition';
import type { PromotionLineItem } from '@weft/types';
import { DEFAULT_CURRENCY, REVISIONED_LINE_ITEM_TYPES } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import React, { useEffect } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { vyFormRegister } from '../../../../../utils/react-hook-forms-utils';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import type { RevisionedLineItemDialogSubmitAction } from '../RevisionedLineItemDialogForm';
import { RevisionedLineItemDialogForm, RevisionedLineItemDialogSubmitActions } from '../RevisionedLineItemDialogForm';
import type { DiscountOption } from './revisioned-line-item-dialog-forms-types/discount-options';
import type { FixedRevisionedLineItemForm } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';
import { PromotionRevisionedLineItemFormSchema, PROMOTION_REVISIONED_LINE_ITEM_TYPES } from './revisioned-line-item-dialog-forms-types/promotion-revisioned-line-item-form';
import type { PercentageRevisionedLineItemForm, PromotionRevisionedLineItemForm, PromotionRevisionedLineItemType } from './revisioned-line-item-dialog-forms-types/promotion-revisioned-line-item-form';

type PromotionRevisionedLineItemDialogFormProps = {
  revisionedLineItemId?: string;
  open: boolean;
  onClose?: () => void;
  invoiceId: string;
  defaultValues?: Partial<PromotionRevisionedLineItemForm>;
  submitAction?: RevisionedLineItemDialogSubmitAction;
  queryKeyToInvalidate?: QueryKey;
  revisionedLineItems: DynamicInvoicePageRevisionedLineItem[]
  promotionLineItems: PromotionLineItem[]
};

export const PromotionRevisionedLineItemDialogForm: React.FC<PromotionRevisionedLineItemDialogFormProps> = ({
  revisionedLineItemId,
  open,
  onClose,
  invoiceId,
  defaultValues,
  revisionedLineItems,
  submitAction,
  queryKeyToInvalidate,
  promotionLineItems,
}) => {
  const form = useForm<PromotionRevisionedLineItemForm>({
    resolver: zodResolver(PromotionRevisionedLineItemFormSchema),
    defaultValues,
  });
  useEffect(() => {
    form.reset( { ...defaultValues });
    form.setValue('type', defaultValues?.type ?? 'FixedPromotion');
  }, [defaultValues]);

  const { getValues, setValue, watch } = form;
  watch('type');

  const handleSelectChange = (event: SelectChangeEvent) => {
    const value = event.target.value as keyof typeof PROMOTION_REVISIONED_LINE_ITEM_TYPES;
    setValue('type', PROMOTION_REVISIONED_LINE_ITEM_TYPES[value]);
  };

  const { dynamicInvoiceData } = useDynamicInvoice();
 
  const revisionedLineItemNameRegister = vyFormRegister(form.register, 'name');
  const revisionedLineItemDescriptionRegister = vyFormRegister(form.register, 'description');
  const promotionTypeRegister = form.register('type');
  const promotionType = getValues('type');

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const lineItemType = (promotionType === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage) ? 'Percentage' : 'Fixed';

  const startAdornment = lineItemType === 'Fixed' ? (
    <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>
  ) : null;

  const revisionedLineItemPriceRegister = promotionType === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage
    ? form.register('percentage', { valueAsNumber: true }) 
    : form.register('price', { valueAsNumber: true });
  const title = submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add discount to this invoice' : 'Update discount in this invoice';
  const submitButtonLabel = submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add Item' : 'Update';
  
  return (
    <RevisionedLineItemDialogForm
      invoiceId={invoiceId}
      revisionedLineItemId={revisionedLineItemId}
      open={open}
      onClose={onClose}
      revisionedLineItemType={promotionType}
      form={form}
      submitAction={submitAction}
      title={title}
      submitButtonLabel={submitButtonLabel}
      queryKeyToInvalidate={queryKeyToInvalidate}
    >
      <TextField
        {...revisionedLineItemNameRegister}
        label="Discount Name"
        placeholder="Type Discount name Here..."
        size='medium'
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[revisionedLineItemNameRegister.name])}
        helperText={form.formState.errors[revisionedLineItemNameRegister.name]?.message}
      />
      <TextField
        {...revisionedLineItemDescriptionRegister}
        label="Description"
        placeholder="Description"
        size='medium'
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[revisionedLineItemDescriptionRegister.name])}
        helperText={form.formState.errors[revisionedLineItemDescriptionRegister.name]?.message}
      />
      <Box sx={{
        display: 'flex',
        gap: '32px',
        padding: '10px',
      }}
      >

        <FormControl
          {...promotionTypeRegister}
          size='medium'
          variant="standard"
          fullWidth
        >
          <InputLabel id="amount-select-label">Amount</InputLabel>
          <Select
            labelId="amount-select-label"
            id="amount-select"
            displayEmpty
            value={lineItemType}
            onChange={handleSelectChange}
            size='medium'
            input={<DesignInput />}
            sx={{
              '& .MuiSelect-select': {
                backgroundColor: '#F6F6F6',
                border: 'none',
                borderRadius: '12px',
              },
            }}
          >
            {getDiscountOptions(revisionedLineItemId, revisionedLineItems, promotionLineItems).map((value) => (
              <MenuItem
                key={value.label.toString()}
                value={value.value}
                disabled={value.disabled}
              >
                {value.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          {...revisionedLineItemPriceRegister}
          defaultValue={0}

          type="number"
          sx={{
            '& .MuiFilledInput-root .MuiInputBase-input': {
              padding: '10px',
            },
          }}
          InputProps={{
            startAdornment,
          }}
          size='medium'
          error={!!getAmountError(promotionType, form.formState.errors)}
        />
      </Box>
      <FormHelperText >
        {getAmountError(promotionType, form.formState.errors) || ' '}
      </FormHelperText>
    </RevisionedLineItemDialogForm>
  );
};

function getDiscountOptions(currentRevisionedLineItemId: string | undefined, revisionedLineItems: DynamicInvoicePageRevisionedLineItem[], promotionLineItems: PromotionLineItem[]): DiscountOption[] {
  const isDisabled = isPercentageDisabled(currentRevisionedLineItemId, revisionedLineItems, promotionLineItems);
  
  return [
    { label: 'Percentage', value: 'Percentage', disabled: isDisabled },
    { label: 'Fixed', value: 'Fixed', disabled: false },
  ];
}

function isPercentageDisabled(currentRevisionedLineItemId: string | undefined, revisionedLineItems: DynamicInvoicePageRevisionedLineItem[], promotionLineItems: PromotionLineItem[]) {
  const percentageRevisionedLineItem = revisionedLineItems.find((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.PercentagePromotion && lineItem.id !== currentRevisionedLineItemId);
  const percentagePromotion = promotionLineItems.find((promotionLineItem) => promotionLineItem.lineItemPromotion.promotionModel.promotionType === 'Percentage');
  
  return isNotNullOrUndefined(percentageRevisionedLineItem) || isNotNullOrUndefined(percentagePromotion);
}

function isFixedPromotionErrors(errors: FieldErrors): errors is FieldErrors<FixedRevisionedLineItemForm> {
  return 'price' in errors;
}

function isPercentagePromotionErrors(errors: FieldErrors): errors is FieldErrors<PercentageRevisionedLineItemForm> {
  return 'percentage' in errors;
}
function getAmountError(type: PromotionRevisionedLineItemType, errors: FieldErrors) {
  if (type === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage && isPercentagePromotionErrors(errors)) {
    return errors.percentage?.message;
  } 
  if (type === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Fixed && isFixedPromotionErrors(errors)) {
    return errors.price?.message;
  }

  return undefined;
}

const DesignInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));